// 帳號 VMS 同步回放的一格播放器（MV.1，docs/mycam/mv-spec.md ①）。
// 兩種來源都交給 hls.js：
//   雲端：/recordings 取時段內片段 → 自己組 VOD m3u8（片段網址經 mediaFetchUrl 同網域，hls.js 直接抓 TS）。
//   本機（SD / 手機本機）：開回放隧道（start_playback）→ 資料通道 loader 播裝置的 /pb.m3u8；片段網址 /seg/<ts_ms>.ts。
// 雲端片段在時間上不一定連續（中間沒錄影），播放清單卻會接起來播 → 「影片秒數 ↔ 真實時間」逐段對應（segMap）。
import type Hls from 'hls.js';
import { apiFetch } from '../api';
import { buildVodM3u8, mediaFetchUrl, type CloudClip } from '../recPlayback/cloudM3u8';
import { makeDataChannelLoaderClass } from '../recPlayback/dataChannelLoader';
import { pbPlaylistUrl } from '../recPlayback/hlsSource';
import { openRemotePlayback, type RemotePlaybackSession } from '../recPlayback/webrtcPlayback';
import type { SpanSource } from './timeline';

/** 一段媒體：在 <video> 的 [mediaStart, mediaStart + dur) 秒 = 真實時間 [wallStartUs, …)。 */
export interface SegMapEntry {
  mediaStart: number;
  dur: number;
  wallStartUs: number;
}

/** 影片秒數 → 真實時間（µs）；不在任何一段 = null。 */
export function mediaToWall(map: SegMapEntry[], t: number): number | null {
  const e = map.find((s) => s.mediaStart <= t && t < s.mediaStart + s.dur) ?? (map.length && t >= map[map.length - 1].mediaStart ? map[map.length - 1] : undefined);
  return e ? e.wallStartUs + (t - e.mediaStart) * 1e6 : null;
}

/** 真實時間 → 影片秒數；沒有錄到那一刻 = null。 */
export function wallToMedia(map: SegMapEntry[], us: number): number | null {
  const e = map.find((s) => s.wallStartUs <= us && us < s.wallStartUs + s.dur * 1e6);
  return e ? e.mediaStart + (us - e.wallStartUs) / 1e6 : null;
}

/** 本機片段網址 /seg/<ts_ms>.ts → 真實起點（µs）。 */
export function localSegWallUs(url: string): number | null {
  const m = /\/seg\/(\d+)\.ts/.exec(url);
  return m ? Number(m[1]) * 1000 : null;
}

const HLS_CONFIG = {
  enableWorker: true,
  maxBufferHole: 1,
  highBufferWatchdogPeriod: 1,
  nudgeOffset: 0.2,
  nudgeMaxRetry: 20,
};

export interface LoadedWindow {
  source: SpanSource;
  fromUs: number;
  toUs: number;
}

export class CellPlayer {
  private hls: Hls | null = null;
  private session: RemotePlaybackSession | null = null;
  private blobUrl: string | null = null;
  private map: SegMapEntry[] = [];
  private seq = 0;
  loaded: LoadedWindow | null = null;

  constructor(private video: HTMLVideoElement, private deviceId: string) {}

  /** 載入一個時段（同來源、時段內已載入就不重載）；回傳是否成功。 */
  async load(source: SpanSource, fromUs: number, toUs: number): Promise<boolean> {
    const seq = ++this.seq;
    this.teardownHls();
    this.map = [];
    this.loaded = null;
    try {
      const { default: HlsCtor } = await import('hls.js');
      if (seq !== this.seq) return false;
      if (!HlsCtor.isSupported()) return false;
      let url: string;
      const config: Record<string, unknown> = { ...HLS_CONFIG };
      if (source === 'cloud') {
        const res = await apiFetch<CloudClip[]>(`/v3/devices/${encodeURIComponent(this.deviceId)}/recordings`, {
          query: { from: fromUs, to: toUs, limit: 200 },
        });
        if (seq !== this.seq) return false;
        const clips = (res.data ?? []).filter((c) => c.url && c.clip_end_ts > c.clip_start_ts).sort((a, b) => a.clip_start_ts - b.clip_start_ts);
        if (clips.length === 0) return false;
        this.blobUrl = URL.createObjectURL(new Blob([buildVodM3u8(clips, clips.map((c) => mediaFetchUrl(c.url)))], { type: 'application/vnd.apple.mpegurl' }));
        url = this.blobUrl;
        let t = 0;
        this.map = clips.map((c) => {
          const dur = (c.clip_end_ts - c.clip_start_ts) / 1e6;
          const e = { mediaStart: t, dur, wallStartUs: c.clip_start_ts };
          t += dur;
          return e;
        });
      } else {
        if (!this.session) this.session = await openRemotePlayback(this.deviceId);
        if (seq !== this.seq) return false;
        const tunnel = this.session.tunnel;
        config.loader = makeDataChannelLoaderClass({ tunnel });
        url = pbPlaylistUrl('', fromUs, toUs);
      }
      const hls = new HlsCtor(config);
      this.hls = hls;
      if (source === 'local') {
        // 播放清單到了才知道每段的真實時間（/seg/<ts_ms>.ts）
        hls.on(HlsCtor.Events.LEVEL_LOADED, (_e: unknown, data: { details?: { fragments?: Array<{ start: number; duration: number; relurl?: string; url?: string }> } }) => {
          const frags = data.details?.fragments ?? [];
          this.map = frags
            .map((f) => ({ mediaStart: f.start, dur: f.duration, wallStartUs: localSegWallUs(f.relurl ?? f.url ?? '') ?? -1 }))
            .filter((e) => e.wallStartUs > 0);
        });
      }
      await new Promise<void>((resolve, reject) => {
        hls.on(HlsCtor.Events.MANIFEST_PARSED, () => resolve());
        hls.on(HlsCtor.Events.ERROR, (_e: unknown, d: { fatal?: boolean; details?: string }) => { if (d.fatal) reject(new Error(d.details ?? 'hls')); });
        hls.loadSource(url);
        hls.attachMedia(this.video);
      });
      if (seq !== this.seq) return false;
      this.loaded = { source, fromUs, toUs };
      return true;
    } catch {
      if (seq === this.seq) this.teardownHls();
      return false;
    }
  }

  /** 現在播到的真實時間（µs）；還沒載入 / 不在任何段 = null。 */
  wallUs(): number | null {
    return this.map.length ? mediaToWall(this.map, this.video.currentTime) : null;
  }

  /** 跳到真實時間 us；那一刻沒錄到回 false。 */
  seekWall(us: number): boolean {
    const t = wallToMedia(this.map, us);
    if (t === null) return false;
    this.video.currentTime = t;
    return true;
  }

  covers(us: number): boolean {
    return wallToMedia(this.map, us) !== null;
  }

  setRate(r: number): void {
    this.video.playbackRate = r;
  }

  play(): void {
    void this.video.play().catch(() => {});
  }

  pause(): void {
    this.video.pause();
  }

  private teardownHls(): void {
    try { this.hls?.destroy(); } catch { /* noop */ }
    this.hls = null;
    if (this.blobUrl) { URL.revokeObjectURL(this.blobUrl); this.blobUrl = null; }
  }

  /** 拿掉這格：停 hls、關回放隧道（釋放裝置的回放連線）。 */
  destroy(): void {
    this.seq++;
    this.teardownHls();
    try { this.session?.stop(); } catch { /* noop */ }
    this.session = null;
    this.loaded = null;
  }
}
